import { useCallback, useEffect, useRef, useState } from "react";
import { requestPracticeAssist } from "../../../api/meetingApi";

const latestAssistantPrompt = (conversation) =>
  [...(conversation || [])].reverse().find((turn) => turn?.ai)?.ai || "";

export default function usePracticeAssist({ meetingId, isTrainingMode, conversationRef }) {
  const mountedRef = useRef(true);
  const enabledRef = useRef(false);
  const requestRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const lifecycleRef = useRef(0);
  const turnOrderRef = useRef(0);
  const latestTurnOrderRef = useRef(0);
  const activeTurnRef = useRef(null);
  const turnContextsRef = useRef(new Map());

  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [messageVersion, setMessageVersion] = useState(0);

  const invalidateRequests = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = null;
    requestSequenceRef.current += 1;
    lifecycleRef.current += 1;
    turnOrderRef.current = 0;
    latestTurnOrderRef.current = 0;
    activeTurnRef.current = null;
    turnContextsRef.current.clear();
  }, []);

  const reset = useCallback(() => {
    invalidateRequests();
    enabledRef.current = false;
    setEnabled(false);
    setMessage("");
    setMessageVersion(0);
  }, [invalidateRequests]);

  useEffect(() => {
    reset();
  }, [isTrainingMode, meetingId, reset]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      invalidateRequests();
    };
  }, [invalidateRequests]);

  const toggle = useCallback(() => {
    if (isTrainingMode) return false;

    const nextEnabled = !enabledRef.current;
    invalidateRequests();
    enabledRef.current = nextEnabled;
    setEnabled(nextEnabled);
    setMessage("");
    setMessageVersion(0);
    return nextEnabled;
  }, [invalidateRequests, isTrainingMode]);

  const capturePrompt = useCallback((event) => {
    if (isTrainingMode || !enabledRef.current) return;
    const order = turnOrderRef.current + 1;
    const itemId = String(event?.item_id || "");
    const context = {
      itemId,
      order,
      prompt: latestAssistantPrompt(conversationRef.current),
    };

    turnOrderRef.current = order;
    latestTurnOrderRef.current = order;
    activeTurnRef.current = context;
    if (itemId) turnContextsRef.current.set(itemId, context);
  }, [conversationRef, isTrainingMode]);

  const clearPrompt = useCallback((event) => {
    const itemId = String(event?.item_id || "");
    if (itemId) turnContextsRef.current.delete(itemId);
    if (!itemId || activeTurnRef.current?.itemId === itemId) activeTurnRef.current = null;
  }, []);

  const requestForTurn = useCallback(
    async (transcript, event) => {
      const normalized = String(transcript || "").replace(/\s+/g, " ").trim();
      if (isTrainingMode || !enabledRef.current || !meetingId || !normalized) return;

      const itemId = String(event?.item_id || "");
      let turn = itemId ? turnContextsRef.current.get(itemId) : activeTurnRef.current;
      if (itemId) turnContextsRef.current.delete(itemId);
      if (turn && activeTurnRef.current?.order === turn.order) activeTurnRef.current = null;

      if (!turn) {
        const order = turnOrderRef.current + 1;
        turnOrderRef.current = order;
        latestTurnOrderRef.current = order;
        turn = {
          itemId,
          order,
          prompt: latestAssistantPrompt(conversationRef.current),
        };
      }
      if (turn.order < latestTurnOrderRef.current) return;

      const sequence = requestSequenceRef.current + 1;
      requestSequenceRef.current = sequence;
      const lifecycle = lifecycleRef.current;
      const turnId = itemId
        ? `${meetingId}-${itemId}`.slice(0, 100)
        : `${meetingId}-${Date.now()}-${sequence}`;

      requestRef.current?.abort();
      const controller = new AbortController();
      requestRef.current = controller;

      try {
        const result = await requestPracticeAssist(
          meetingId,
          { turnId, prompt: turn.prompt, transcript: normalized },
          controller.signal
        );
        if (
          !mountedRef.current ||
          controller.signal.aborted ||
          !enabledRef.current ||
          lifecycle !== lifecycleRef.current ||
          turn.order !== latestTurnOrderRef.current ||
          sequence !== requestSequenceRef.current ||
          result.turnId !== turnId ||
          !result.message
        ) {
          return;
        }

        setMessage(result.message);
        setMessageVersion((current) => current + 1);
      } catch (error) {
        if (!controller.signal.aborted) {
          console.warn("Practice Assist request failed", error?.message || "Unknown error");
        }
      } finally {
        if (sequence === requestSequenceRef.current && mountedRef.current) {
          requestRef.current = null;
        }
      }
    },
    [conversationRef, isTrainingMode, meetingId]
  );

  return {
    enabled,
    message,
    messageVersion,
    toggle,
    capturePrompt,
    clearPrompt,
    requestForTurn,
    reset,
  };
}
